import React, { Component } from "react";
import "./OwS3Uploader.scss";
// import ReactS3Uploader from "react-s3-uploader";
import axios, { AxiosProgressEvent, AxiosRequestConfig } from "axios";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import INotification from "../../../models/INotification";
import IUserUI from "../../../models/IUserUI";
import ISupplierModel from "../../../models/ISupplierModel";
import IVendorModel from "../../../models/IVendorModel";
import IGetSignedUrlDTO from "../../../models/DTO/IGetSignedUrlDTO";
import FileUploadResourceTypeEnum from "../../../models/FileUploadResourceTypeEnum";
import ICreateFileUploadDTO from "../../../models/DTO/ICreateFileUploadDTO";
import IOnCreateFileUploadDTO from "../../../models/DTO/IOnCreateFileUploadDTO";
import IOnGetSignedUrlDTO from "../../../models/DTO/IOnGetSignedUrlDTO";
import * as _ from "lodash-es";
import Dropzone from "react-dropzone";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import S3UploadTypeEnum from "../../../models/S3UploadTypeEnum";
import OwTranslate from "../OwTranslate/OwTranslate";
import OwButton from "../OwButton/OwButton";
import OwLabel from "../OwLabel/OwLabel";
import { Socket } from "socket.io-client";
import ky, { Options, Progress } from "ky";

type Props = {
    socket: Socket;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel?: IVendorModel;
    orderGuid?: string;
    catalogGuid?: string;
    catalogItemGuid?: string;
    shipmentGroupGuid?: string;
    purchaseOrderGuid?: string;
    isReadOnly: boolean;
    resourceType: FileUploadResourceTypeEnum;
    dropOnly: boolean;
    imagesOnly?: boolean;
    imageSeq?: number;
    notifyFn?: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    isProcessedFn?: (isProcessed: boolean) => void;
    isSpec?: boolean;
    isWhiteLabelLogo?: boolean;
    isWhiteLabelLightLogo?: boolean;
    isBrandedLogo?: boolean;
    isS3Upload?: boolean;
    isDocLogo?: boolean;
    /**
     * If not supplied and isS3Upload is true, then defaults to ITEM_TEMPLATE
     */
    s3UploadType?: S3UploadTypeEnum;
    maxFileSize?: number;
    /** When true, clears existing price bands for price groups found in the CSV before creating new ones */
    clearExistingForPriceGroups?: boolean;
    allowPaste?: boolean;
    /** One-line drop zone that reports its state in place of the label; no size or type footer. */
    compact?: boolean;
    /** Translation key for the compact drop zone's label. */
    dropLabelKey?: string;
    /** Translation key shown in the compact zone when a file of the wrong type is dropped. */
    fileTypeInvalidKey?: string;
    /** Called once the server has recorded the upload, so the owner can refresh what it shows. */
    onUploaded?: () => void;
    testId?: string;
    /** Compact only: content that sits inside the drop target above the zone line (a preview, say). */
    children?: React.ReactNode;
    /** Compact only: a note shown beside the zone line, such as the accepted file types. */
    zoneAside?: React.ReactNode;
};

type State = {
    signedUrl?: string;
    file?: any;
    contentType?: string;
    attachment_type_id?: number;
    bucket?: string;
    key?: string;
    name?: string;
    url?: string;
    fileDropRejected: boolean;
    fileTypeRejected: boolean;
    uploadProgressPercent: string;
    isProcessing: boolean;
    isComplete: boolean;
    isFocused: boolean;
    /** A file has been accepted and its upload has not finished (or failed) yet. */
    inFlight: boolean;
    /** The last upload failed or stalled; the compact zone says so until the next file. */
    uploadFailed: boolean;
};

/** How long the uploader waits for the server to answer a signed-url request before giving up. */
const SIGNED_URL_TIMEOUT_MS = 30000;

class OwS3Uploader extends Component<Props, State> {
    uploadInput: any;
    containerRef = React.createRef<HTMLDivElement>();

    static defaultProps = {
        dropOnly: false,
        imagesOnly: false,
    };

    readonly state: State = {
        fileDropRejected: false,
        fileTypeRejected: false,
        uploadProgressPercent: "0",
        isProcessing: false,
        isComplete: false,
        isFocused: false,
        inFlight: false,
        uploadFailed: false,
    };

    signedUrlTimer: ReturnType<typeof setTimeout> | null = null;

    componentDidMount(): void {
        this.props.socket.on(EndpointsEnum.FileUploadOnGetSignedUrl, this.onGetSignedURL);
        this.props.socket.on(EndpointsEnum.FileUploadOnCreateFileUpload, this.onCreateFileUpload);
        this.props.socket.on("onLambdaImageProcessed", this.onLambdaImageProcessed);

        if (this.props.allowPaste && this.containerRef.current) {
            this.containerRef.current.addEventListener("paste", this.onPaste);
        }
    }

    componentWillUnmount(): void {
        this.props.socket.removeListener(EndpointsEnum.FileUploadOnGetSignedUrl, this.onGetSignedURL);
        this.props.socket.removeListener(EndpointsEnum.FileUploadOnCreateFileUpload, this.onCreateFileUpload);
        this.props.socket.removeListener("onLambdaImageProcessed", this.onLambdaImageProcessed);
        this.clearSignedUrlTimer();

        if (this.props.allowPaste && this.containerRef.current) {
            this.containerRef.current.removeEventListener("paste", this.onPaste);
        }
    }

    clearSignedUrlTimer = () => {
        if (this.signedUrlTimer) {
            clearTimeout(this.signedUrlTimer);
            this.signedUrlTimer = null;
        }
    };

    /** Gives the block back after a failed or stalled upload; the compact zone shows the failure until the next file. */
    failUpload = (reason: unknown) => {
        console.error("Upload failed: ", reason);
        this.clearSignedUrlTimer();
        this.setState({ inFlight: false, isProcessing: false, uploadProgressPercent: "0", isComplete: false, uploadFailed: true });
    };

    onPaste = (event: ClipboardEvent) => {
        const items = event.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.kind === "file" && item.type.startsWith("image/")) {
                const file = item.getAsFile();
                if (file) {
                    event.preventDefault();
                    this.uploadFile([file]);
                    return;
                }
            }
        }
    };

    onPasteFromButton = async () => {
        try {
            const clipboardItems = await navigator.clipboard.read();
            for (const clipboardItem of clipboardItems) {
                const imageType = clipboardItem.types.find((type) => type.startsWith("image/"));
                if (imageType) {
                    const blob = await clipboardItem.getType(imageType);
                    const file = new File([blob], "pasted-image.png", { type: imageType });
                    this.uploadFile([file]);
                    return;
                }
            }
        } catch (err) {
            console.warn("Clipboard read failed:", err);
        }
    };

    onLambdaImageProcessed = (data: any) => {
        const { name, sku, image_seq, catalogItemGuid } = data;

        if (this.props.imagesOnly && this.props.catalogItemGuid === catalogItemGuid && Number(this.props.imageSeq) === Number(image_seq)) {
            this.setState({
                isProcessing: false,
                isComplete: true,
            });

            if (this.props.notifyFn) {
                const notification: INotification = {
                    position: NotificationPositionEnum.bl,
                    message: `${name} ${sku}`,
                    // autoDismiss: true,
                    level: NotificationLevelEnum.success,
                    title: "successfully_processed_image",
                };

                this.props.notifyFn(null, notification, false);
            }

            if (this.props.isProcessedFn) {
                this.props.isProcessedFn(true);
            }

            setTimeout(() => {
                this.setState({
                    isComplete: false,
                });
            }, 2000);
        }
    };

    onUploadStart = (file: any, next: any) => {
        // console.log("onUploadStart", file);

        next(file);
    };

    onSignedUrl = (data: any) => {
        // console.log("onSignedUrl", data);
    };

    onUploadProgressKy = (data: Progress) => {
        console.log("onUploadProgressKy: ", data);

        if (data) {
            const uploadProgressPercent = data.percent.toFixed(2);

            this.setState({
                isComplete: false,
                uploadProgressPercent,
            });
        }
    };

    onUploadProgress = (data: AxiosProgressEvent) => {
        // console.log("onUploadProgress", data);
        if (data.lengthComputable) {
            const uploadProgressPercent = ((data.loaded / (data.total || 1)) * 100).toFixed(2);

            this.setState(
                {
                    isComplete: false,
                    uploadProgressPercent,
                },
                () => {}
            );
        }
    };

    onUploadError = (data: any) => {
        // console.log("onUploadError", data);
    };

    onUploadFinish = (data: any) => {
        // console.log("onUploadFinish", data);
    };

    getSignedUrl = (file: any) => {
        const payload: IGetSignedUrlDTO = {
            userGuid: this.props.userModel.guid,
            userToken: this.props.userModel.token,
            supplierGuid: this.props.supplierModel.guid,
            file: {
                name: file.name,
                type: file.type,
            },
            resourceType: this.props.resourceType,
        };

        if (this.props.orderGuid) {
            payload.orderGuid = this.props.orderGuid;
        }

        if (this.props.vendorModel) {
            payload.vendorGuid = this.props.vendorModel.guid;
        }

        if (this.props.catalogItemGuid) {
            payload.catalogItemGuid = this.props.catalogItemGuid;
        }

        if (this.props.catalogGuid) {
            payload.catalogGuid = this.props.catalogGuid;
        }

        if (this.props.shipmentGroupGuid) {
            payload.shipmentGroupGuid = this.props.shipmentGroupGuid;
        }

        if (this.props.purchaseOrderGuid) {
            payload.purchaseOrderGuid = this.props.purchaseOrderGuid;
        }

        this.props.socket.emit(EndpointsEnum.FileUploadGetSignedUrl, payload);
    };

    onGetSignedURL = (data: IOnGetSignedUrlDTO) => {
        const filename = this.state.file ? decodeURI(encodeURIComponent(this.state.file.name).replace(/%20/g, "+")) : "";

        if (this.state.file && decodeURI(data.originalName) === this.state.file.name) {
            this.clearSignedUrlTimer();
            this.setState(
                {
                    signedUrl: data.signedUrl,
                    attachment_type_id: data.attachment_type_id,
                    bucket: data.bucket,
                    key: data.key,
                    name: data.name,
                    url: data.url,
                },
                async () => {
                    try {
                        await this.uploadWithSignedURL();
                    } catch (err) {
                        this.failUpload(err);
                    }
                }
            );
        }
    };

    createFileUpload = (data: ICreateFileUploadDTO): void => {
        this.props.socket.emit(EndpointsEnum.FileUploadCreateFileUpload, data);

        this.setState({
            isProcessing: true,
        });
    };

    onCreateFileUpload = (data: IOnCreateFileUploadDTO): void => {
        // console.log("onCreateFileUpload", data);
        // console.log("this.state.url", this.state.url);
        // console.log("data.url", data.url);
        if (data.url === this.state.url) {
            this.setState({
                isProcessing: false,
                uploadProgressPercent: "0",
                isComplete: true,
                inFlight: false,
            });

            if (this.props.onUploaded) {
                this.props.onUploaded();
            }

            if (this.props.compact) {
                // The compact zone shows the success in its one line; give it back after a moment.
                setTimeout(() => this.setState({ isComplete: false }), 2500);
            }
        }
    };

    /**
     * The compact variant: the whole block (children included) is the drop target, and the zone
     * line under it opens the file picker. Its label doubles as the status, and a thin bar shows
     * upload progress.
     */
    compactComponents = () => {
        const percent = Number(this.state.uploadProgressPercent) || 0;
        const uploading = percent > 0 && !this.state.isComplete;
        // From the moment a file is accepted until the server records it, the block takes no new
        // drops or picks: the upload reads shared state after the PUT resolves, so a second file
        // would corrupt the first's record.
        const busy = this.state.inFlight || uploading || this.state.isProcessing;
        const maxSize = this.props.maxFileSize || 26214400;

        let labelKey = this.props.dropLabelKey || "drag_and_drop_or_click_to_select_file";

        if (this.state.fileTypeRejected) {
            labelKey = this.props.fileTypeInvalidKey || "file_type_invalid";
        } else if (this.state.fileDropRejected) {
            labelKey = "file_too_big_limit";
        } else if (this.state.uploadFailed) {
            labelKey = "upload_failed";
        } else if (this.state.isProcessing) {
            labelKey = "processing";
        } else if (uploading) {
            labelKey = "uploading";
        } else if (this.state.isComplete) {
            labelKey = "successfully_uploaded";
        }

        const rejected = this.state.fileTypeRejected || this.state.fileDropRejected || this.state.uploadFailed;
        const className = ["OwS3Uploader compact", busy ? "busy" : "", rejected ? "rejected" : "", this.state.isComplete ? "complete" : ""]
            .filter(Boolean)
            .join(" ");

        let iconClass = "fad fa-cloud-arrow-up";

        if (rejected) {
            iconClass = "fad fa-triangle-exclamation";
        } else if (busy) {
            iconClass = "fas fa-circle-notch fa-spin";
        } else if (this.state.isComplete) {
            iconClass = "fad fa-check";
        }

        return (
            <Dropzone
                maxSize={maxSize}
                multiple={false}
                noClick={true}
                noKeyboard={true}
                disabled={busy}
                onDrop={(acceptedFiles) => this.uploadFile(acceptedFiles)}
                onDropRejected={(files) => this.dropRejected(files)}>
                {({ getRootProps, getInputProps, isDragActive, open }) => (
                    <div {...getRootProps({ className: className + (isDragActive ? " drag-over" : "") })} data-testid={this.props.testId}>
                        <input {...getInputProps()} />
                        {this.props.children}
                        <div className={"OwS3Uploader-foot"}>
                            <div
                                className={"OwS3Uploader-zone"}
                                role={"button"}
                                tabIndex={busy ? -1 : 0}
                                aria-disabled={busy}
                                onClick={busy ? undefined : open}
                                onKeyDown={(event) => {
                                    if (!busy && (event.key === "Enter" || event.key === " ")) {
                                        event.preventDefault();
                                        open();
                                    }
                                }}>
                                <i className={iconClass} aria-hidden={true} />
                                {this.state.fileDropRejected && !this.state.fileTypeRejected ? (
                                    <span>
                                        <OwTranslate
                                            id={labelKey}
                                            defaultMessage={labelKey}
                                            values={{ size: Math.round(maxSize / 1024 / 1024) }}
                                        />
                                    </span>
                                ) : (
                                    <OwLabel title={labelKey} noBottomMargin={true} />
                                )}
                            </div>
                            {this.props.zoneAside}
                            {uploading ? (
                                <div
                                    className={"OwS3Uploader-progress"}
                                    role={"progressbar"}
                                    aria-valuenow={Math.round(percent)}
                                    aria-valuemin={0}
                                    aria-valuemax={100}>
                                    <div className={"bar"} style={{ width: `${percent}%` }} />
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}
            </Dropzone>
        );
    };

    uploadWithSignedURL = async (): Promise<any> => {
        if (this.state.signedUrl && this.state.file) {
            const config: AxiosRequestConfig = {
                headers: {
                    "Content-Type": this.state.contentType,
                },
                onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                    this.onUploadProgress(progressEvent);
                },
            };

            const response = await axios.put(this.state.signedUrl, this.state.file, config);

            // Would like to use ky here, but the progress piece isn't working?
            // const options: Options = {
            //     // headers: {
            //     //     "Content-Type": this.state.contentType,
            //     // },
            //     onUploadProgress: (progress) => {
            //         console.log(progress);
            //         this.onUploadProgressKy(progress);
            //     },
            //     body: this.state.file,
            // }
            //
            // const response = await ky.put(this.state.signedUrl, options)

            if (response.status === 200) {
                const payload: ICreateFileUploadDTO = {
                    // @ts-expect-error
                    attachment_type_id: this.state.attachment_type_id,
                    bucket: this.state.bucket!,
                    key: this.state.key!,
                    name: this.state.name!,
                    size: this.state.file.size,
                    url: this.state.url!,
                    eTag: response.headers.etag,
                    // eTag: response.headers.get('etag') || "",
                    userGuid: this.props.userModel.guid,
                    userToken: this.props.userModel.token,
                    supplierGuid: this.props.supplierModel.guid,
                };

                if (this.props.resourceType === FileUploadResourceTypeEnum.bulkImages) {
                    payload.fileUploadResourceTypeId = this.props.resourceType;
                }

                if (this.props.isSpec) {
                    payload.is_spec = true;
                    payload.fileUploadResourceTypeId = this.props.resourceType;
                }

                if (this.props.isWhiteLabelLogo) {
                    payload.is_white_label = true;
                    payload.fileUploadResourceTypeId = this.props.resourceType;
                }

                if (this.props.isWhiteLabelLightLogo) {
                    payload.is_white_label_light = true;
                    payload.fileUploadResourceTypeId = this.props.resourceType;
                }

                if (this.props.isBrandedLogo) {
                    payload.is_branded_logo = true;
                    payload.fileUploadResourceTypeId = this.props.resourceType;
                }

                if (this.props.isDocLogo) {
                    payload.is_doc_logo = true;
                    payload.fileUploadResourceTypeId = this.props.resourceType;
                }

                if (!_.isNil(this.props.imageSeq)) {
                    payload.image_seq = this.props.imageSeq;
                }

                if (!_.isNil(this.props.orderGuid)) {
                    payload.orderGuid = this.props.orderGuid;
                }

                if (!_.isNil(this.props.purchaseOrderGuid)) {
                    payload.purchaseOrderGuid = this.props.purchaseOrderGuid;
                }

                if (!_.isNil(this.props.vendorModel)) {
                    payload.vendorGuid = this.props.vendorModel.guid;
                    payload.fileUploadResourceTypeId = this.props.resourceType;
                }

                if (!_.isNil(this.props.catalogItemGuid)) {
                    payload.catalogItemGuid = this.props.catalogItemGuid;
                }

                if (!_.isNil(this.props.catalogGuid)) {
                    payload.catalogGuid = this.props.catalogGuid;
                }

                if (this.props.isS3Upload) {
                    payload.is_s3_upload = true;

                    if (this.props.s3UploadType) {
                        payload.s3_upload_type = this.props.s3UploadType;
                    } else {
                        switch (this.props.resourceType) {
                            case FileUploadResourceTypeEnum.childItemTemplateCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.CHILD_ITEM_TEMPLATE;
                                break;

                            case FileUploadResourceTypeEnum.catalogItemCategoriesCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.ITEM_CATEGORIES;
                                break;

                            case FileUploadResourceTypeEnum.priceBandsCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.PRICE_BANDS_CSV;
                                break;

                            case FileUploadResourceTypeEnum.bulkItemOptionsCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.BULK_ITEM_OPTIONS;
                                break;

                            case FileUploadResourceTypeEnum.catalogAttributesCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.CATALOG_ATTRIBUTES_CSV;
                                break;

                            case FileUploadResourceTypeEnum.bulkKitBuilderCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.BULK_KIT_BUILDER;
                                break;

                            case FileUploadResourceTypeEnum.priceBandsBulkApplyCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.PRICE_BANDS_BULK_APPLY;
                                break;

                            case FileUploadResourceTypeEnum.itemOptionsAssignCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.ITEM_OPTIONS_ASSIGN;
                                break;

                            case FileUploadResourceTypeEnum.priceListCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.PRICE_LIST;
                                break;

                            case FileUploadResourceTypeEnum.catalogItemImagesUrlCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.CATALOG_ITEM_IMAGES_URL_CSV;
                                break;

                            case FileUploadResourceTypeEnum.bomCsv:
                                payload.s3_upload_type = S3UploadTypeEnum.BOM_CSV;
                                break;

                            default:
                                payload.s3_upload_type = S3UploadTypeEnum.ITEM_TEMPLATE;
                                break;
                        }
                    }
                }

                if (this.props.clearExistingForPriceGroups) {
                    payload.clear_existing_for_price_groups = true;
                }

                this.createFileUpload(payload);

                return response;
            } else {
                throw new Error("Invalid Response");
            }
        }
    };

    fileUploadComponents = () => {
        const headers = {};
        const queryParams = {};

        const maxSize = this.props.maxFileSize || 26214400; // 25 MB

        let fileOptions = "JPG, BMP, PNG, SVG, PDF, DOC, DOCX, XLS, XLSX";

        if (this.props.imagesOnly) {
            fileOptions = "JPG, BMP, PNG, SVG";
        }

        return (
            <div
                className={"OwS3Uploader" + (this.state.isFocused ? " paste-focused" : "")}
                ref={this.props.allowPaste ? this.containerRef : undefined}
                tabIndex={this.props.allowPaste ? 0 : undefined}
                onFocus={this.props.allowPaste ? () => this.setState({ isFocused: true }) : undefined}
                onBlur={this.props.allowPaste ? () => this.setState({ isFocused: false }) : undefined}>
                <Dropzone
                    maxSize={maxSize}
                    multiple={false}
                    onDrop={(acceptedFiles) => this.uploadFile(acceptedFiles)}
                    onDropRejected={(files) => this.dropRejected(files)}>
                    {({ getRootProps, getInputProps }) => (
                        <section>
                            <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                <p className={"pointer"}>
                                    <u>
                                        <OwTranslate
                                            defaultMessage={"drag_and_drop_or_click_to_select_file"}
                                            id={"drag_and_drop_or_click_to_select_file"}
                                        />
                                    </u>
                                </p>
                            </div>
                        </section>
                    )}
                </Dropzone>

                {this.props.allowPaste && (
                    <OwButton
                        className={"btn-link btn-sm text-muted"}
                        centerIconClass={"fad fa-paste"}
                        iconTitle={"paste_from_clipboard"}
                        fn={this.onPasteFromButton}
                    />
                )}

                <div className={"mt-2 mb-2 dropzone-detail-section"}>
                    <div className={"upload-progress-bar " + (this.state.isProcessing ? "d-none" : "")}>
                        <div className={"bar"} style={{ width: `${this.state.uploadProgressPercent}%` }}></div>
                    </div>

                    <div className={"mt-1 " + (this.state.isProcessing ? "d-none" : "")}>
                        <div className={"text-muted text-small"}>{this.state.uploadProgressPercent}%</div>
                    </div>

                    <div className={"processing " + (this.state.isProcessing ? "" : "d-none")}>
                        <OwLabel title={"processing"} leftIconClass={"fas fa-circle-notch fa-spin"} />
                    </div>
                </div>

                <div className={"text-success " + (this.state.isComplete ? "" : "d-none")}>
                    <OwLabel title={"successfully_uploaded"} />
                </div>

                <div className={"d-flex justify-content-between error-section"}>
                    <span className={this.props.dropOnly ? "d-none" : ""}>
                        <OwLabel className={"faded-orange mt-2"} title={"max_size_50_mb"} />
                    </span>
                    <OwLabel
                        className={"text-danger mt-2 " + (this.state.fileDropRejected ? "" : "d-none")}
                        title={"file_drop_rejected_too_big"}
                    />
                    <OwLabel className={"text-danger mt-2 " + (this.state.fileTypeRejected ? "" : "d-none")} title={"file_type_invalid"} />
                </div>

                <div className={"text-muted text-small " + (this.props.dropOnly ? "d-none" : "")}>
                    <OwTranslate defaultMessage={"allowed_file_types"} id={"allowed_file_types"} />:<br />
                    {fileOptions}
                </div>
            </div>
        );
    };

    readOnlyComponents = () => {
        return (
            <div className={"d-flex text-warning"}>
                <div className={"me-2"}>
                    <i className={"far fa-lock-alt"} />
                </div>
                <OwLabel title={"file_uploads_read_only_once_order_confirmed"} />
            </div>
        );
    };

    uploadFile = async (files: any) => {
        const file = files[0];
        console.log("uploadFile", file);

        if (_.isNil(file)) {
            return;
        }

        if (this.props.imagesOnly) {
            switch (file.type) {
                case "image/jpeg":
                case "image/bmp":
                case "image/png":
                case "image/svg+xml":
                    break;

                default:
                    this.setState({
                        fileTypeRejected: true,
                    });
                    return;
            }
        }

        if (file) {
            this.setState(
                {
                    file,
                    contentType: file.type,
                    fileDropRejected: false,
                    fileTypeRejected: false,
                    isComplete: false,
                    uploadFailed: false,
                    inFlight: true,
                },
                () => {
                    this.getSignedUrl(file);
                    // If the server never answers, give the block back rather than leaving it busy for good.
                    this.clearSignedUrlTimer();
                    this.signedUrlTimer = setTimeout(() => this.failUpload("No signed url within the time limit"), SIGNED_URL_TIMEOUT_MS);
                }
            );
        }
    };

    dropRejected = (files: any) => {
        this.setState({
            fileDropRejected: true,
        });
    };

    render() {
        let view;

        if (this.props.isReadOnly) {
            view = this.readOnlyComponents();
        } else if (this.props.compact) {
            view = this.compactComponents();
        } else {
            view = this.fileUploadComponents();
        }

        return view;
    }
}

export default OwS3Uploader;
